import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Paperclip, ArrowRight } from "lucide-react";
import { CONTACT } from "../data/content";
import Reveal from "./Reveal";

const Contact = () => {
  const [sent, setSent] = useState(false);
  const [fileName, setFileName] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    timerRef.current = setTimeout(() => {
      setSent(false);
      setForm({ name: "", email: "", phone: "", message: "" });
      setFileName("");
    }, 4000);
  };

  const inputCls =
    "w-full bg-transparent border-b border-royal/20 py-3 text-royal placeholder-titanium focus:border-gold outline-none transition-colors duration-300 font-body text-lg";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-beige"
    >
      <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Info */}
        <div className="lg:col-span-5">
          <Reveal>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="font-accent text-sm uppercase tracking-[0.28em] text-gold">
                Get In Touch
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-royal text-4xl md:text-5xl lg:text-6xl leading-[1.08]">
              Let's build the next{" "}
              <span className="italic text-gold-gradient">landmark</span> together.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 text-titanium leading-relaxed max-w-md text-lg">
              {CONTACT.tagline}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 border-t border-royal/10 pt-10">
              <p className="font-display text-2xl md:text-3xl text-royal leading-snug">
                Prefer a direct line?{" "}
                <span className="italic text-gold-gradient">Send us a message.</span>
              </p>
              <p className="mt-4 text-titanium leading-relaxed text-lg max-w-md">
                Share a few details in the form and our team will personally
                review your inquiry and respond to discuss your partnership or
                investment interest.
              </p>
              <div className="mt-8 flex items-center gap-3 text-gold">
                <ArrowRight size={20} />
                <span className="font-accent text-sm uppercase tracking-[0.22em]">
                  Complete the form to get started
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-beige-light border border-royal/10 p-8 md:p-12 shadow-[0_30px_60px_-30px_rgba(22,40,75,0.25)]"
            >
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-8">
                <div className="md:col-span-1">
                  <label className="font-accent text-xs uppercase tracking-[0.24em] text-titanium">
                    Full Name
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="John Carter"
                    data-testid="form-name"
                    className={inputCls}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="font-accent text-xs uppercase tracking-[0.24em] text-titanium">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@company.com"
                    data-testid="form-email"
                    className={inputCls}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="font-accent text-xs uppercase tracking-[0.24em] text-titanium">
                    Phone No.
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+1 (000) 000-0000"
                    data-testid="form-phone"
                    className={inputCls}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="font-accent text-xs uppercase tracking-[0.24em] text-titanium">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Tell us about your partnership interest..."
                    data-testid="form-message"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <div className="md:col-span-2">
                  <label
                    htmlFor="file-upload"
                    data-testid="form-file-label"
                    className="flex items-center gap-3 cursor-pointer text-titanium hover:text-gold transition-colors w-fit"
                  >
                    <Paperclip size={16} />
                    <span className="font-accent text-sm uppercase tracking-[0.18em]">
                      {fileName || "Attach File"}
                    </span>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="form-submit"
                disabled={sent}
                className="group mt-10 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-royal text-beige-light font-accent uppercase tracking-[0.18em] text-sm px-10 py-4 hover:bg-gold transition-all duration-300 disabled:opacity-70"
              >
                {sent ? (
                  <>
                    <CheckCircle2 size={18} /> Message Sent
                  </>
                ) : (
                  <>
                    Submit Inquiry
                    <Send size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              {sent && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-testid="form-success"
                  className="mt-4 text-gold text-lg"
                >
                  Thank you — our team will be in touch shortly.
                </motion.p>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
